import { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Users, BedDouble, Bath, Ruler, Check, MapPin, Clock, ShieldQuestion } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { properties, AMENITY_LABELS } from "@/data/site";
import { formatEUR } from "@/lib/booking";
import PropertyReviews from "@/components/PropertyReviews";
import Gallery from "@/components/Gallery";

const SCHEMA_TYPE = { villa: "VacationRental", apartment: "Apartment", bnb: "BedAndBreakfast", room: "LodgingBusiness" };

export default function PropertyDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const property = properties.find((p) => p.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!property) return;
    document.title = `${property.name} · Martelli Rentals`;
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "property-jsonld";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": SCHEMA_TYPE[property.category] || "LodgingBusiness",
      name: property.name,
      description: property.longDescription,
      address: { "@type": "PostalAddress", addressLocality: property.city, addressRegion: property.region, addressCountry: "IT" },
      image: property.gallery,
      amenityFeature: property.amenities.map((a) => ({ "@type": "LocationFeatureSpecification", name: AMENITY_LABELS[a] || a })),
    });
    document.head.appendChild(script);
    return () => { document.getElementById("property-jsonld")?.remove(); };
  }, [property]);

  if (!property) {
    return (
      <>
        <Header />
        <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4 px-6 text-center">
          <h1 className="font-heading text-4xl text-brand-black">Struttura non trovata</h1>
          <Link to="/" className="text-brand-olive underline">Torna alla home</Link>
        </div>
        <Footer />
      </>
    );
  }

  const similar = properties.filter((p) => p.id !== property.id && (p.region === property.region || p.category === property.category)).slice(0, 3);

  return (
    <>
      <Header />
      <main className="pt-20">
        {/* Galleria */}
        <section className="max-w-7xl mx-auto px-6 md:px-12 pt-8">
          <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm text-brand-brown hover:text-brand-olive mb-6">
            <ArrowLeft className="w-4 h-4" /> Indietro
          </button>
          <div className="mt-2">
            <Gallery images={property.gallery} name={property.name} captions={property.captions} />
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-6 md:px-12 py-12 grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <span className="inline-flex items-center gap-1 text-xs bg-brand-ivory text-brand-brown px-3 py-1.5 rounded-full"><MapPin className="w-3 h-3" /> {property.city}, {property.region}</span>
            <h1 className="font-heading font-light text-4xl md:text-5xl text-brand-black mt-4">{property.name}</h1>
            <p className="text-brand-sage mt-1">{property.type}</p>
            {property.cin ? <p className="text-xs text-brand-brown/60 mt-1">CIN {property.cin}{property.cir ? ` · CIR ${property.cir}` : ""}</p> : null}

            <div className="flex flex-wrap gap-6 mt-6 text-sm text-brand-black/80">
              <span className="inline-flex items-center gap-2"><Users className="w-5 h-5 text-brand-sage" /> Fino a {property.maxGuests} ospiti</span>
              {property.bedrooms ? <span className="inline-flex items-center gap-2"><BedDouble className="w-5 h-5 text-brand-sage" /> {property.bedrooms} {property.category === "bnb" ? "suite" : property.bedrooms === 1 ? "camera" : "camere"}</span> : null}
              {property.bathrooms ? <span className="inline-flex items-center gap-2"><Bath className="w-5 h-5 text-brand-sage" /> {property.bathrooms} {property.bathrooms === 1 ? "bagno" : "bagni"}</span> : null}
              {property.sizeSqm ? <span className="inline-flex items-center gap-2"><Ruler className="w-5 h-5 text-brand-sage" /> {property.sizeSqm} m²</span> : null}
            </div>

            <p className="mt-8 text-brand-brown leading-relaxed text-lg">{property.longDescription}</p>
            {property.highlight && <p className="mt-4 bg-brand-gold/15 text-brand-brown rounded-xl px-5 py-4 text-sm">{property.highlight}</p>}

            {property.rooms?.length ? (
              <div className="mt-10">
                <h2 className="font-heading text-2xl text-brand-black mb-4">Camere e unità</h2>
                <ul className="space-y-2 text-brand-brown">{property.rooms.map((r, i) => <li key={i} className="flex items-center gap-2"><Check className="w-4 h-4 text-brand-sage" /> {r}</li>)}</ul>
              </div>
            ) : null}

            <div className="mt-10">
              <h2 className="font-heading text-2xl text-brand-black mb-4">Servizi</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {property.amenities.map((a) => <span key={a} className="flex items-center gap-2 text-sm text-brand-brown"><Check className="w-4 h-4 text-brand-sage" /> {AMENITY_LABELS[a] || a}</span>)}
              </div>
            </div>

            <div className="mt-10 grid sm:grid-cols-2 gap-6">
              <div className="border border-brand-ivory rounded-2xl p-5">
                <h3 className="font-medium text-brand-black inline-flex items-center gap-2"><Clock className="w-4 h-4 text-brand-sage" /> Check-in / Check-out</h3>
                <p className="text-sm text-brand-brown mt-2">Check-in dalle 15:00 · Check-out entro le 10:00. {/* TODO: orari specifici per struttura */}</p>
              </div>
              <div className="border border-brand-ivory rounded-2xl p-5">
                <h3 className="font-medium text-brand-black inline-flex items-center gap-2"><ShieldQuestion className="w-4 h-4 text-brand-sage" /> Cancellazione</h3>
                <p className="text-sm text-brand-brown mt-2">Le condizioni dipendono dalla tariffa scelta e vengono mostrate prima della conferma. {/* TODO: policy definitiva */}</p>
              </div>
            </div>

            <div className="mt-10">
              <h2 className="font-heading text-2xl text-brand-black mb-4">Regole della casa</h2>
              <ul className="space-y-2 text-sm text-brand-brown">
                <li>· Vietato fumare negli ambienti interni</li>
                <li>· Rispetto della quiete negli orari serali</li>
                <li>· Animali su richiesta, in base alla struttura</li>
              </ul>
            </div>

            {/* Mappa (zona esatta solo per Terra di Gaia e Costa Masciarelli; approssimativa per gli alloggi privati) */}
            {(() => {
              const MAPS = {
                "la-terra-di-gaia": { q: "La Terra di Gaia, Magliano de' Marsi", link: "https://maps.app.goo.gl/GyDeQBx1JABhReky7", precise: true },
                "costa-masciarelli": { q: "Costa Masciarelli, L'Aquila", link: "https://maps.app.goo.gl/voaTvDG2iPZ3N7oG9", precise: true },
                "appartamento-duomo": { q: "Piazza Duomo, L'Aquila", link: "https://maps.app.goo.gl/4sheKQkDxkquuhzg7", precise: false },
                "appartamento-corridoni": { q: "Via Corridoni, L'Aquila", link: "", precise: false },
                "homas-roma": { q: "Via Monte Serrone, Roma", link: "", precise: false },
                "la-brigantessa-apartment": { q: "Valdevarri, Abruzzo", link: "https://maps.app.goo.gl/4iZoSBMFXiXstEai8", precise: false },
              };
              const m = MAPS[property.slug] || { q: `${property.city}, ${property.region}`, link: "", precise: false };
              const src = `https://www.google.com/maps?q=${encodeURIComponent(m.q)}&z=${m.precise ? 15 : 13}&output=embed`;
              return (
                <div className="mt-10">
                  <h2 className="font-heading text-2xl text-brand-black mb-4">Dove si trova</h2>
                  <iframe
                    title={`Mappa ${property.name}`}
                    src={src}
                    loading="lazy"
                    className="w-full h-64 rounded-2xl border border-brand-ivory"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                  <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
                    <a href={m.link || `https://www.google.com/maps/search/${encodeURIComponent(m.q)}`} target="_blank" rel="noopener noreferrer" className="text-sm text-brand-olive underline underline-offset-4">Apri in Google Maps</a>
                  </div>
                  {!m.precise && (
                    <p className="mt-2 text-xs text-brand-brown/70">La posizione mostrata è approssimativa. L’indirizzo completo e le indicazioni per l’accesso vengono comunicati dopo la conferma della prenotazione.</p>
                  )}
                </div>
              );
            })()}
          </div>

          {/* Prenotazione */}
          <aside className="lg:col-span-1">
            <div className="sticky top-28 border border-brand-ivory rounded-2xl p-6 bg-white">
              <div className="mb-4">
                <div className="font-heading text-2xl text-brand-olive">Verifica disponibilità e tariffe</div>
              </div>
              <p className="text-sm text-brand-brown mb-5">Tariffe e disponibilità aggiornate in tempo reale nel sistema di prenotazione.</p>
              <Link to="/#booking" onClick={() => setTimeout(() => document.getElementById("booking")?.scrollIntoView({ behavior: "smooth" }), 400)} className="block text-center bg-brand-olive text-white hover:bg-brand-black transition-colors rounded-full py-3.5 font-medium">
                Verifica disponibilità
              </Link>
            </div>
          </aside>
        </section>

        {/* Recensioni reali della struttura */}
        <section className="max-w-7xl mx-auto px-6 md:px-12 pb-4">
          <PropertyReviews slug={property.slug} />
        </section>

        {similar.length > 0 && (
          <section className="max-w-7xl mx-auto px-6 md:px-12 pb-20">
            <h2 className="font-heading text-3xl text-brand-black mb-6">Strutture simili</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {similar.map((s) => (
                <Link key={s.id} to={`/strutture/${s.slug}`} className="group block border border-brand-ivory rounded-2xl overflow-hidden bg-white">
                  <div className="h-48 overflow-hidden"><img src={s.featuredImage} alt={s.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" /></div>
                  <div className="p-5"><h3 className="font-heading text-xl text-brand-black">{s.name}</h3><p className="text-sm text-brand-sage">{s.city}</p></div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}
